import { jsx, Fragment } from 'react/jsx-runtime';
import { useState, useEffect, useMemo, useContext, createContext, isValidElement } from 'react';
import { useCreation, usePersistFn, useMount } from '@ausz/hooks';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import { UrlUtils, omit, pick, merge, request } from '@ausz/utils';
import { Outlet, Route } from 'react-router-dom';

var appsMap = new Map();
function registerApps(apps) {
  apps.forEach(function (item) {
    appsMap.set(item.activeRule, item);
  });
}
var Navigator = /*#__PURE__*/function () {
  function Navigator(navigateFn) {
    _classCallCheck(this, Navigator);
    this.navigateFn = navigateFn;
  }
  _createClass(Navigator, [{
    key: "redirectTo",
    value: function redirectTo(url, options) {
      // redirectUrl 存在，则表示跳转到外部 app，使用原生跳转，否则使用 router 内部跳转
      var redirectUrl = this.getEntryUrl(url);
      var fn = (redirectUrl ? this.externalNavigate : this.navigateFn) || this.externalNavigate;
      fn(redirectUrl || url, options);
    }
  }, {
    key: "getRedirectUrl",
    value: function getRedirectUrl(url) {
      return this.getEntryUrl(url) || url;
    }
  }, {
    key: "getEntryUrl",
    value: function getEntryUrl(url) {
      var fullUrl = UrlUtils.getFullRequestUrl(url);
      var relativeUrl = UrlUtils.toRelativeUrl(window.location.origin, fullUrl);
      // 只判断第一级目录，也就是说不支持设置 a/b 这种 apps key
      var firstToken = relativeUrl === '/' ? '/' : "/".concat(relativeUrl.split('/').filter(Boolean)[0]);
      var params = appsMap.get(firstToken);
      var entry = params === null || params === void 0 ? void 0 : params.entry;
      if (!entry) {
        return '';
      }
      var index = url.lastIndexOf(firstToken);
      var suffix = url.substring(index + firstToken.length);
      return entry + suffix;
    }
  }, {
    key: "externalNavigate",
    value: function externalNavigate(url, options) {
      var target = (options === null || options === void 0 ? void 0 : options.target) || '_self';
      if (target === '_self') {
        window.location.href = url;
      } else {
        window.open(url, target);
      }
    }
  }]);
  return Navigator;
}();

var DefaultConfig = {
  loginPage: '/',
  loginRedirectURL: '/',
  logoutRedirectURL: '/',
  host: '/api',
  loginByPhoneURL: '/v1/auth/login/phone',
  loginByWechatURL: '/v1/auth/login/wechat',
  logoutURL: '/v1/auth/logout',
  registerURL: 'v1/auth/register',
  userURL: '/v1/auth/users/current',
  // if your change this, change @ausz/utils/request either, there have hard code.
  storageKey: 'fish'
};
var redirectKey = 'redirect';
var DefaultAuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  access: {}
};
var JWT_HEADER_NAME = 'XAuthorization';
var AuthClient = /*#__PURE__*/function () {
  function AuthClient(config) {
    _classCallCheck(this, AuthClient);
    var defaultCopy = Object.assign({}, DefaultConfig);
    this.config = merge(defaultCopy, config);
    this.authState = DefaultAuthState;
    this.callbacks = [];
    this.navigator = new Navigator();
    this.instance = request.create(this.config.host);
  }
  _createClass(AuthClient, [{
    key: "login",
    value: function login(params) {
      var _this = this;
      var method = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'phone';
      var _this$config = this.config,
        loginByPhoneURL = _this$config.loginByPhoneURL,
        loginByWechatURL = _this$config.loginByWechatURL;
      var url = method === 'phone' ? loginByPhoneURL : loginByWechatURL;
      var fixedParam = method === 'phone' ? omit(params, 'wechatCode') : pick(params, 'wechatCode');
      return this.instance.post(url, fixedParam, {
        getResponse: true,
        skipErrorHandler: true
      }).then(function (response) {
        _this.handleLogin(response);
        return response.data;
      });
    }
  }, {
    key: "register",
    value: function register(params) {
      var _this2 = this;
      return this.instance.post(this.config.registerURL, params, {
        getResponse: true,
        skipErrorHandler: true
      }).then(function (response) {
        _this2.handleLogin(response);
        return response.data;
      });
    }
  }, {
    key: "handleLogin",
    value: function handleLogin(response) {
      var token = response.headers[JWT_HEADER_NAME.toLocaleLowerCase()];
      if (!token) {
        throw new Error('token required');
      }
      localStorage.setItem(this.config.storageKey, token.replace('Bearer ', ''));
      var user = response.data;
      this.setState({
        isAuthenticated: true,
        token: token,
        user: user
      });
      var query = new URLSearchParams(window.location.search);
      var redirect = query.get(redirectKey);
      var url = redirect || this.config.loginRedirectURL;
      this.redirectTo(url);
    }
  }, {
    key: "logout",
    value: function logout() {
      var _this3 = this;
      return this.instance.post(this.config.logoutURL).then(function () {
        _this3.setState(DefaultAuthState);
        localStorage.removeItem(_this3.config.storageKey);
        _this3.redirectTo(_this3.config.logoutRedirectURL);
      });
    }
  }, {
    key: "redirectToLoginPage",
    value: function redirectToLoginPage() {
      var redirect = encodeURIComponent(window.location.href);
      var loginUrl = this.navigator.getRedirectUrl(this.config.loginPage);
      // 如果当前已经是登录页，则无需跳转
      if (window.location.pathname !== UrlUtils.getPathname(loginUrl)) {
        var loginPage = "".concat(loginUrl, "?").concat(redirectKey, "=").concat(redirect);
        this.redirectTo(loginPage);
      }
    }
  }, {
    key: "getUser",
    value: function getUser() {
      var _this4 = this;
      var skipError = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var redirectToLogin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var fetchUser = function fetchUser() {
        var token = localStorage.getItem(_this4.config.storageKey);
        return _this4.instance.get(_this4.config.userURL, {
          skipErrorHandler: skipError
        }).then(function (user) {
          _this4.setState({
            isAuthenticated: true,
            token: token,
            user: user
          });
          return user;
        }).catch(function () {
          if (redirectToLogin) {
            _this4.redirectToLoginPage();
          }
        });
      };
      return this.trySyncToken().catch(function () {
        // eslint-disable-next-line no-console
        console.warn('%c登录页指向的服务并未启动，正常尝试使用旧凭证进行访问！！！', 'background:red;color:white');
      }).finally(fetchUser);
    }
  }, {
    key: "setUser",
    value: function setUser(user) {
      this.setState({
        user: user
      });
    }
  }, {
    key: "modifySelfInfo",
    value: function modifySelfInfo(param) {
      var _this5 = this;
      var curUser = this.authState.user;
      if (!curUser) {
        return Promise.reject(new Error('Please login first'));
      }
      var newUser = Object.assign(Object.assign({}, curUser), param);
      return this.instance.patch(this.config.userURL, param).then(function () {
        return _this5.setState({
          user: newUser
        });
      });
    }
  }, {
    key: "getAuthState",
    value: function getAuthState() {
      return this.authState;
    }
  }, {
    key: "subscribe",
    value: function subscribe(fn) {
      this.callbacks.push(fn);
    }
  }, {
    key: "unsubscribe",
    value: function unsubscribe(fn) {
      var index = this.callbacks.indexOf(fn);
      if (index !== -1) {
        this.callbacks.splice(index, 1);
      }
    }
  }, {
    key: "getConfig",
    value: function getConfig() {
      return this.config;
    }
  }, {
    key: "trySyncToken",
    value: function trySyncToken() {
      var _this6 = this;
      if (process.env.NODE_ENV !== 'development') {
        return Promise.resolve();
      }
      return new Promise(function (resolve, reject) {
        var iframe = document.createElement('iframe');
        var loginUrl = _this6.navigator.getRedirectUrl(_this6.config.loginPage);
        iframe.src = loginUrl;
        iframe.width = '0px';
        iframe.height = '0px';
        iframe.style.border = '0px';
        document.body.append(iframe);
        var clean = function clean() {
          try {
            document.body.removeChild(iframe);
            // eslint-disable-next-line no-empty
          } catch (_a) {}
        };
        iframe.onerror = function (e) {
          reject(e);
          clean();
        };
        window.addEventListener('message', function (ev) {
          var _a, _b, _c;
          var key = _this6.config.storageKey;
          if (((_a = ev.data) === null || _a === void 0 ? void 0 : _a.type) === _this6.config.storageKey) {
            localStorage.setItem(key, (_b = ev.data) === null || _b === void 0 ? void 0 : _b.payload);
            resolve((_c = ev.data) === null || _c === void 0 ? void 0 : _c.payload);
            clean();
          }
        });
      });
    }
  }, {
    key: "redirectTo",
    value: function redirectTo(url, options) {
      this.navigator.redirectTo(url, options);
    }
  }, {
    key: "setState",
    value: function setState(state) {
      this.authState = Object.assign(Object.assign({}, this.authState), state);
      this.emitStateChange();
    }
  }, {
    key: "emitStateChange",
    value: function emitStateChange() {
      var _this7 = this;
      this.callbacks.forEach(function (fn) {
        return fn(Object.assign({}, _this7.authState));
      });
    }
  }]);
  return AuthClient;
}();

var AuthContext = /*#__PURE__*/createContext({});
function AuthProvider(_ref) {
  var children = _ref.children,
    onAuthRequired = _ref.onAuthRequired,
    authConfig = _ref.authConfig;
  var authClient = useCreation(function () {
    return new AuthClient(authConfig);
  }, []);
  var _useState = useState(function () {
      return authClient.getAuthState();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    authState = _useState2[0],
    setAuthState = _useState2[1];
  useEffect(function () {
    var handler = function handler(newState) {
      setAuthState(newState);
    };
    authClient.subscribe(handler);
    return function () {
      authClient.unsubscribe(handler);
    };
  }, [authClient]);
  var value = useMemo(function () {
    return {
      authClient: authClient,
      authState: authState,
      onAuthRequired: onAuthRequired
    };
  }, [authClient, authState, onAuthRequired]);
  return jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
}
function useAuth() {
  return useContext(AuthContext);
}

var UserRoleEnum;
(function (UserRoleEnum) {
  UserRoleEnum["GENERAL"] = "general";
  UserRoleEnum["LIMITED"] = "limited";
  UserRoleEnum["ADMIN"] = "admin";
})(UserRoleEnum || (UserRoleEnum = {}));
var CreditUsingEnum;
(function (CreditUsingEnum) {
  /* 时间制 */
  CreditUsingEnum["PASSPORT"] = "passport";
  /* 积分制 */
  CreditUsingEnum["PURSE"] = "purse";
})(CreditUsingEnum || (CreditUsingEnum = {}));
var UnlimitedProjCount = -1; // 不限数量
var InfiniteAvailable = 'infinite'; // 无限期

function useAccess() {
  var auth = useAuth();
  return auth.authState.access;
}
function useUser() {
  var auth = useAuth();
  return auth.authState.user;
}
function useUserState() {
  var auth = useAuth();
  var user = auth.authState.user;
  var setUser = usePersistFn(function (newUser) {
    return auth.authClient.setUser(newUser);
  });
  return useMemo(function () {
    return [user, setUser];
  }, [setUser, user]);
}
function useCreditToken() {
  var auth = useAuth();
  if (!auth.authState.user) {
    return 0;
  }
  var purse = auth.authState.user.credit.purse;
  return purse.reduce(function (prev, cur) {
    return prev + cur.num;
  }, 0);
}
function useExpired() {
  var auth = useAuth();
  if (!auth.authState.user) {
    return false;
  }
  var _auth$authState$user$ = auth.authState.user.credit,
    passport = _auth$authState$user$.passport,
    using = _auth$authState$user$.using;
  return using === CreditUsingEnum.PASSPORT && !passport.isAvailable;
}
function useRolesCheck() {
  var user = useUser();
  var fn = usePersistFn(function () {
    var allowRoles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Object.values(UserRoleEnum);
    if (!user) {
      return false;
    }
    return allowRoles.some(function (item) {
      return item === user.role;
    });
  });
  return fn;
}
/**
 * @description 用于解决跨域 token 无法共享问题，暂只在本地环境使用
 * @author Ethan
 * @export
 */
function useSyncAuthDevOnly() {
  var _useAuth = useAuth(),
    authClient = _useAuth.authClient;
  useEffect(function () {
    if (process.env.NODE_ENV === 'development' && window.parent) {
      var key = authClient.getConfig().storageKey;
      var token = localStorage.getItem(key);
      window.parent.postMessage({
        type: key,
        payload: token
      }, '*');
    }
  }, [authClient]);
}
/**
 * @description 获取用户登录态
 * 1. 如果未验证，尝试获取用户信息，如 token 失效，则跳转到登录页
 * 2. 如果已验证，do nothing
 * @author Ethan
 */
function useFetchUserIfRequired() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$skipError = _ref.skipError,
    skipError = _ref$skipError === void 0 ? false : _ref$skipError,
    redirectToLogin = _ref.redirectToLogin;
  var _useAuth2 = useAuth(),
    authClient = _useAuth2.authClient,
    authState = _useAuth2.authState;
  useMount(function () {
    if (!authState.isAuthenticated) {
      authClient.getUser(skipError, redirectToLogin);
    }
  });
}

function authentication(params, userAccess) {
  var requiredPermissions = params.requiredPermissions,
    oneOfPerm = params.oneOfPerm;
  if (Array.isArray(requiredPermissions) && requiredPermissions.length) {
    var count = 0;
    requiredPermissions.forEach(function (item) {
      if (userAccess[item]) {
        count += 1;
      }
    });
    return oneOfPerm ? count > 0 : count === requiredPermissions.length;
  }
  return true;
}

function convertReactElement(node) {
  if (! /*#__PURE__*/isValidElement(node)) {
    return jsx(Fragment, {
      children: node
    });
  }
  return node;
}
/**
 * @description 根据认证信息自定义元素的渲染
 * @author Ethan
 * @export
 */
function Access(_ref) {
  var requiredPermissions = _ref.requiredPermissions,
    oneOfPerm = _ref.oneOfPerm,
    fallback = _ref.fallback,
    children = _ref.children;
  var access = useAccess();
  var accessible = useMemo(function () {
    return authentication({
      requiredPermissions: requiredPermissions,
      oneOfPerm: oneOfPerm
    }, access);
  }, [oneOfPerm, requiredPermissions, access]);
  if (typeof children === 'function') {
    return convertReactElement(children(accessible));
  }
  if (accessible) {
    return convertReactElement(children);
  }
  if (fallback) {
    return convertReactElement(fallback);
  }
  return null;
}

function registerAppsEnv() {
  var isDev = process.env.NODE_ENV === 'development';
  var hostname = window.location.hostname;
  var homePagePort = process.env.REACT_APP_PORT_HOMEPAGE || 3000;
  var basementPort = process.env.REACT_APP_PORT_BASEMENT || 3001;
  var foundationPort = process.env.REACT_APP_PORT_FOUNDATION || 3002;
  var evaluationPort = process.env.REACT_APP_PORT_EVALUATION || 3002;
  registerApps([{
    entry: isDev ? "//".concat(hostname, ":").concat(homePagePort) : '/',
    activeRule: '/'
  }, {
    entry: isDev ? "//".concat(hostname, ":").concat(basementPort, "/basement") : '/basement',
    activeRule: '/basement'
  }, {
    entry: isDev ? "//".concat(hostname, ":").concat(foundationPort, "/foundation") : '/foundation',
    activeRule: '/foundation'
  }, {
    entry: isDev ? "//".concat(hostname, ":").concat(evaluationPort, "/evaluation") : '/evaluation',
    activeRule: '/evaluation'
  }]);
}

function PrivateOutlet() {
  var _useAuth = useAuth(),
    authState = _useAuth.authState;
  useFetchUserIfRequired();
  if (!authState.isAuthenticated) {
    return null;
  }
  return jsx(Outlet, {});
}

/**
 * @description 保护路由
 * 存在的问题：Routes 组件做了检查，子组件必须是 Route，二次封装的 PrivateRoute 会直接报错。貌似是 react-router v6 阻止了这种方式
 * @author Ethan
 * @param {RouteProps} props
 */
function PrivateRoute(props) {
  var _useAuth = useAuth(),
    authState = _useAuth.authState;
  useFetchUserIfRequired();
  if (!authState.isAuthenticated) {
    return null;
  }
  return jsx(Route, Object.assign({}, props));
}

function Guard(_ref) {
  var _ref$roles = _ref.roles,
    roles = _ref$roles === void 0 ? Object.values(UserRoleEnum) : _ref$roles,
    children = _ref.children,
    limited = _ref.limited,
    fallback = _ref.fallback;
  var user = useUser();
  var expired = useExpired();
  var accessible = useMemo(function () {
    if (!user) {
      return false;
    }
    return roles.some(function (role) {
      return role === user.role;
    }) && !(expired && limited);
  }, [limited, roles, user, expired]);
  if (accessible) {
    return jsx(Fragment, {
      children: children
    });
  }
  if (typeof fallback === 'function') {
    return fallback();
  }
  return jsx(Fragment, {
    children: fallback
  });
}

export { Access, AuthClient, AuthProvider, CreditUsingEnum, Guard, InfiniteAvailable, PrivateOutlet, PrivateRoute, UnlimitedProjCount, UserRoleEnum, authentication, registerApps, registerAppsEnv, useAccess, useAuth, useCreditToken, useExpired, useFetchUserIfRequired, useRolesCheck, useSyncAuthDevOnly, useUser, useUserState };
